import moment from 'moment';
import { validations } from '@unite-us/app-components';
import { getMinServiceDate, getMaxServiceDate, MAX_AGE_LABEL } from './periodOfServiceDateBounds';
import { PERIOD_TYPE } from '../constants';

const toDate = (d) => (d instanceof Date ? d : new Date(d));

const isInvalidDate = (d) => {
  if (d == null || d === '') return false;
  return Number.isNaN(toDate(d).getTime());
};

const toCalendarDay = (value) => moment.utc(value, 'MM/DD/YYYY', true).startOf('day');

const getDateRangeError = (value, label) => {
  if (!value || isInvalidDate(value)) return null;

  const date = toCalendarDay(value);
  const today = getMaxServiceDate().toDate();
  const twoYearsAgo = getMinServiceDate().toDate();

  if (date.isAfter(today)) return `${label} must be no later than today`;
  if (date.isBefore(twoYearsAgo)) return `${label} cannot be more than ${MAX_AGE_LABEL} in the past`;

  return null;
};

const isSameMonth = (start, end) => {
  const date1 = toDate(start);
  const date2 = toDate(end);
  return date1.getUTCMonth() === date2.getUTCMonth() && date1.getUTCFullYear() === date2.getUTCFullYear();
};

const validateHours = (value) => validations.isRequired(value);

const validateMinutes = (value) => validations.isRequired(value);

export const validateStructuredInteractionForm = (values = {}) => {
  const errors = {};

  const codeConceptError = validations.isRequired(values.codeConceptId);
  if (codeConceptError) errors.codeConceptId = codeConceptError;

  const hoursError = validateHours(values.hours);
  if (hoursError) errors.hours = hoursError;

  const minutesError = validateMinutes(values.minutes);
  if (minutesError) errors.minutes = minutesError;

  if (!errors.hours && !errors.minutes) {
    const hours = parseInt(values.hours, 10) || 0;
    const minutes = parseInt(values.minutes, 10) || 0;

    if (hours === 0 && minutes === 0) {
      errors.minutes = validations.isGreaterThan(values.minutes, null, 0);
    }
  }

  if (values.periodType === PERIOD_TYPE.SINGLE) {
    const singleDateRequired = validations.isRequired(values.singleDate);

    if (singleDateRequired) {
      errors.singleDate = singleDateRequired;
    } else if (isInvalidDate(values.singleDate)) {
      errors.singleDate = 'Enter a valid date';
    } else {
      const singleDateError = getDateRangeError(values.singleDate, 'Date');
      if (singleDateError) errors.singleDate = singleDateError;
    }
  }

  if (values.periodType === PERIOD_TYPE.RANGE) {
    const rangeStartRequired = validations.isRequired(values.rangeStart, 'Required');

    if (rangeStartRequired) {
      errors.rangeStart = rangeStartRequired;
    } else if (isInvalidDate(values.rangeStart)) {
      errors.rangeStart = 'Enter a valid start date';
    } else {
      const rangeStartError = getDateRangeError(values.rangeStart, 'Start date');
      if (rangeStartError) errors.rangeStart = rangeStartError;
    }

    if (values.rangeStart) {
      const rangeEndRequired = validations.isRequired(values.rangeEnd, 'Required');

      if (rangeEndRequired) {
        errors.rangeEnd = rangeEndRequired;
      } else if (isInvalidDate(values.rangeEnd)) {
        errors.rangeEnd = 'Enter a valid end date';
      } else {
        const rangeEndError = getDateRangeError(values.rangeEnd, 'End date');
        if (rangeEndError) errors.rangeEnd = rangeEndError;
      }
    }

    if (values.rangeStart && values.rangeEnd && !errors.rangeStart && !errors.rangeEnd) {
      if (!isSameMonth(values.rangeStart, values.rangeEnd)) {
        errors.rangeEnd = 'End date must be in the same month as the start date';
      }
    }
  }

  return errors;
};
